import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

// The account as the sign-in response returns it: enough for a user menu and a
// decision about administrative affordances, and deliberately nothing else. The
// response carries no password material and no active flag -- see
// `docs/user-management.md`. `isAdmin` is spelled the way the rest of this
// code spells things; it arrives as `is_admin` and is renamed on the way in.
export interface SessionUser {
  id: string
  name: string
  email: string
  username: string
  isAdmin: boolean
}

// A status rather than a boolean, so that reading it says which of the two
// states the application is in without also having to know what a null token
// means.
export type SessionStatus = 'signed-out' | 'signed-in'

export interface SessionState {
  token: string | null
  user: SessionUser | null
  status: SessionStatus
}

// What starting a session takes. The server sends the token beside the account
// rather than inside it, and naming the two apart here is what keeps an
// unrelated field the server might add later -- an `active` flag, say -- out of
// the store by accident.
export interface Session {
  token: string
  user: SessionUser
}

// Where the session is kept between page loads. Local storage rather than a
// cookie because there is no CSRF protection here, and the trade-off is worth
// stating rather than hiding: a cookie the server marks unreadable is not
// something a cross-site script can take, while a token in local storage is.
// `metalist.` so it cannot collide with anything else served from this origin.
export const SESSION_STORAGE_KEY = 'metalist.session'

// Reads back what a previous visit stored. Returns nothing for an entry that is
// absent, unparseable or carries no token, because local storage is writable by
// anything running on the origin and a hand-edited entry must not be able to
// present the application with a session the server never issued.
function storedSession (): Session | null {
  const stored = window.localStorage.getItem(SESSION_STORAGE_KEY)

  if (stored === null) {
    return null
  }

  let parsed
  try {
    parsed = JSON.parse(stored)
  } catch (error) {
    return null
  }

  if (parsed === null || typeof parsed.token !== 'string' || parsed.token === '') {
    return null
  }

  return parsed as Session
}

// The store is built from this rather than empty, which is what makes a reload
// stay signed in: the token is read before the first request goes out, so the
// request interceptor has one to send without a round trip to ask for it.
function restoredState (): SessionState {
  const session = storedSession()

  if (session === null) {
    return { token: null, user: null, status: 'signed-out' }
  }

  // The token is what authenticates, so it is what decides the status. A
  // stored session whose account is missing is still signed in as far as the
  // server is concerned, and it will say so on the next request.
  return { token: session.token, user: session.user || null, status: 'signed-in' }
}

export default new Vuex.Store<SessionState>({
  state: restoredState(),

  // The mutations write local storage as well as the state, rather than leaving
  // that to whatever called them. There are two places a session can end -- a
  // sign-in and a sign-out -- and both go through here, so this is the one place
  // the stored copy can be kept from drifting away from the store's.
  mutations: {
    sessionStarted (state: SessionState, session: Session) {
      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))

      state.token = session.token
      state.user = session.user
      state.status = 'signed-in'
    },

    sessionCleared (state: SessionState) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY)

      state.token = null
      state.user = null
      state.status = 'signed-out'
    },

    // A change to the account's own editable fields, for a caller that has just
    // been told by the server that it has been made.
    //
    // Additive rather than a widening of `sessionStarted`, because this is not a
    // new session: the token is the one already in hand, and `update_own_profile`
    // revokes nothing, so re-signing-in to refresh a name would be absurd. Partial
    // on purpose too -- the profile read knows more about the account than the
    // session holds (two timestamps, an active flag), and none of that belongs in
    // an identity, so what arrives here is exactly the two fields a person may
    // change and no more.
    //
    // Writes local storage for the same reason the other two do: it is the one
    // place the stored copy can be kept from drifting away from the store's, and a
    // reload after a name change would otherwise come back showing the old one.
    userUpdated (state: SessionState, changes: Pick<SessionUser, 'name' | 'email'>) {
      const user = state.user === null ? null : { ...state.user, ...changes }

      if (state.token !== null) {
        window.localStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify({ token: state.token, user })
        )
      }

      state.user = user
    }
  },

  getters: {
    // What the router and the views ask. Reading the status directly would mean
    // every one of them knowing what the two status values are.
    signedIn: (state: SessionState): boolean => state.status === 'signed-in',

    // Whether the session belongs to an administrator, which is what decides
    // whether the administrative affordances are offered at all. `false` rather
    // than an error for a session that is not signed in: "not an admin" is
    // already true of somebody with no session, and a guard asks the question on
    // the way to checking that there is one.
    //
    // The flag as the sign-in response stated it, not as a claim read off the
    // token -- see `Metalist.AuthToken` for why it is not one, and `guards.ts`
    // for what that means about how much this is worth. The server is the
    // authority on administration; this decides what to offer.
    admin: (state: SessionState): boolean => state.user !== null && state.user.isAdmin
  }
})
